<mat-card class="chart-card">
  <mat-card-header>
    <mat-card-title>{{ 'analytics.hiringRateDashboard' | transloco }}</mat-card-title>
    <mat-card-subtitle>{{ 'analytics.comprehensiveView' | transloco }}</mat-card-subtitle>
  </mat-card-header>

  <mat-card-content>
    <!-- SSR placeholder -->
    <div *ngIf="!isBrowser" class="ssr-placeholder">
      <p>{{ 'analytics.loadingDashboard' | transloco }}</p>
    </div>

    <!-- Render dashboard only in browser -->
    <ng-container *ngIf="isBrowser">
      <!-- Loading state -->
      <div *ngIf="loading" class="loading-overlay">
        <mat-spinner diameter="50"></mat-spinner>
        <p>{{ 'analytics.loadingAnalytics' | transloco }}</p>
      </div>

      <!-- Error state -->
      <div *ngIf="error && !loading" class="error-message">
        <p>{{ error }}</p>
      </div>

      <!-- Dashboard Grid -->
      <div *ngIf="!loading && !error" class="dashboard-grid">
        <!-- Metric Cards - Full Width Top Row -->
        <div class="metrics-section">
          <div class="metric-card">
            <div class="metric-icon hired">
              <i class="fas fa-check-circle"></i>
            </div>
            <div class="metric-content">
              <p class="metric-label">{{ 'analytics.totalHired' | transloco }}</p>
              <h2 class="metric-value">{{ totalHired }}</h2>
            </div>
          </div>

          <div class="metric-card">
            <div class="metric-icon applications">
              <i class="fas fa-file-alt"></i>
            </div>
            <div class="metric-content">
              <p class="metric-label">{{ 'analytics.totalApplications' | transloco }}</p>
              <h2 class="metric-value">{{ totalApplications }}</h2>
            </div>
          </div>

          <div class="metric-card">
            <div class="metric-icon rate">
              <i class="fas fa-arrow-up"></i>
            </div>
            <div class="metric-content">
              <p class="metric-label">{{ 'analytics.hiringRate' | transloco }}</p>
              <h2 class="metric-value">{{ hiringRate.toFixed(1) }}%</h2>
            </div>
          </div>

          <div class="metric-card">
            <div class="metric-icon days">
              <i class="fas fa-clock"></i>
            </div>
            <div class="metric-content">
              <p class="metric-label">{{ 'analytics.avgDaysToHire' | transloco }}</p>
              <h2 class="metric-value">{{ avgDaysToHire.toFixed(0) }}</h2>
            </div>
          </div>
        </div>

        <!-- Time Series Chart - Full Width Second Row -->
        <div class="breakdown-section time-series-section">
          <div class="chart-header">
            <h3>{{ 'analytics.timeSeries' | transloco }}</h3>
            <p class="chart-subtitle">{{ 'analytics.hiringRateOverTime' | transloco }}</p>
          </div>
          <div class="chart-container">
            <ngx-charts-line-chart
              *ngIf="timeSeriesData.length > 0"
              [scheme]="timeSeriesColorScheme"
              [results]="timeSeriesData"
              [gradient]="gradient"
              [xAxis]="showXAxis"
              [yAxis]="showYAxis"
              [legend]="showLegend"
              [legendPosition]="legendPosition"
              [showXAxisLabel]="showXAxisLabel"
              [showYAxisLabel]="showYAxisLabel"
              [xAxisLabel]="'analytics.xAxisDate' | transloco"
              [yAxisLabel]="'analytics.yAxisCount' | transloco"
              [animations]="animations"
              [yAxisTickFormatting]="yAxisTickFormatting"
              (select)="onSelect($event)"></ngx-charts-line-chart>

            <div *ngIf="timeSeriesData.length === 0" class="no-data">
              <p>{{ 'analytics.noTimeSeriesData' | transloco }}</p>
            </div>
          </div>
        </div>

        <!-- Breakdown Tables - Flexible Layout -->
        <div class="breakdown-tables-container">
          <div class="breakdown-section table-section">
            <div class="chart-header">
              <h3>{{ 'analytics.byCategory' | transloco }}</h3>
              <p class="chart-subtitle">{{ 'analytics.topNinePlusOthers' | transloco }}</p>
            </div>
            <div class="table-container">
              <table *ngIf="categoryData.length > 0" class="breakdown-table">
                <thead>
                  <tr>
                    <th>{{ 'analytics.category' | transloco }}</th>
                    <th>{{ 'analytics.hired' | transloco }}</th>
                    <th>{{ 'analytics.applications' | transloco }}</th>
                    <th>{{ 'analytics.rate' | transloco }}</th>
                    <th>{{ 'analytics.avgDays' | transloco }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let item of categoryData">
                    <td class="name-cell">{{ item.name }}</td>
                    <td>{{ item.hired }}</td>
                    <td>{{ item.applications }}</td>
                    <td class="rate-cell">{{ item.rate.toFixed(1) }}%</td>
                    <td>{{ item.avgDays.toFixed(0) }}</td>
                  </tr>
                </tbody>
              </table>

              <div *ngIf="categoryData.length === 0" class="no-data">
                <p>{{ 'analytics.noCategoryData' | transloco }}</p>
              </div>
            </div>
          </div>

          <div class="breakdown-section table-section">
            <div class="chart-header">
              <h3>{{ 'analytics.byPosition' | transloco }}</h3>
              <p class="chart-subtitle">{{ 'analytics.topNinePlusOthers' | transloco }}</p>
            </div>
            <div class="table-container">
              <table *ngIf="positionData.length > 0" class="breakdown-table">
                <thead>
                  <tr>
                    <th>{{ 'analytics.position' | transloco }}</th>
                    <th>{{ 'analytics.hired' | transloco }}</th>
                    <th>{{ 'analytics.applications' | transloco }}</th>
                    <th>{{ 'analytics.rate' | transloco }}</th>
                    <th>{{ 'analytics.avgDays' | transloco }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let item of positionData">
                    <td class="name-cell">{{ item.name }}</td>
                    <td>{{ item.hired }}</td>
                    <td>{{ item.applications }}</td>
                    <td class="rate-cell">{{ item.rate.toFixed(1) }}%</td>
                    <td>{{ item.avgDays.toFixed(0) }}</td>
                  </tr>
                </tbody>
              </table>

              <div *ngIf="positionData.length === 0" class="no-data">
                <p>{{ 'analytics.noPositionData' | transloco }}</p>
              </div>
            </div>
          </div>

          <div class="breakdown-section table-section">
            <div class="chart-header">
              <h3>{{ 'analytics.byLocation' | transloco }}</h3>
              <p class="chart-subtitle">{{ 'analytics.topNinePlusOthers' | transloco }}</p>
            </div>
            <div class="table-container">
              <table *ngIf="locationData.length > 0" class="breakdown-table">
                <thead>
                  <tr>
                    <th>{{ 'analytics.location' | transloco }}</th>
                    <th>{{ 'analytics.hired' | transloco }}</th>
                    <th>{{ 'analytics.applications' | transloco }}</th>
                    <th>{{ 'analytics.rate' | transloco }}</th>
                    <th>{{ 'analytics.avgDays' | transloco }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let item of locationData">
                    <td class="name-cell">{{ item.name }}</td>
                    <td>{{ item.hired }}</td>
                    <td>{{ item.applications }}</td>
                    <td class="rate-cell">{{ item.rate.toFixed(1) }}%</td>
                    <td>{{ item.avgDays.toFixed(0) }}</td>
                  </tr>
                </tbody>
              </table>

              <div *ngIf="locationData.length === 0" class="no-data">
                <p>{{ 'analytics.noLocationData' | transloco }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ng-container>
  </mat-card-content>
</mat-card>
